import React, { useState, useRef, useEffect } from 'react'
import { trackEvent } from 'utils/analytics'
import UserAvatar from '../shared/UserAvatar'
import AutoResizeTextarea from '../shared/AutoResizeTextarea'
import CommunityCommentList from './CommunityCommentList'
import CommunityCommentInput from './CommunityCommentInput'
import CommunityPostDetailModal from './CommunityPostDetailModal'
import {
  updateCommunityPost,
  deleteCommunityPost,
  pinCommunityPost,
  toggleCommunityLike,
  recordCommunityPostView,
} from 'utils/teachingTeamApi'
import { buildTeamShareUrl } from 'utils'
import { compressImages } from 'utils/imageCompressor'
import ShareButton from './ShareButton'
import PromotionLinkModal from './PromotionLinkModal'
import Linkify, { VideoThumbnail } from '../shared/Linkify'
import ViewStats from '../shared/ViewStats'
import { detectVideoPlatform } from 'utils/videoUtils'
import { timeAgo } from 'utils/timeAgo'

function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={onClose}>
      <div className="absolute top-4 right-4 flex items-center gap-3 z-10">
        <a
          href={src}
          download
          onClick={(e) => e.stopPropagation()}
          className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          title="Download"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
          </svg>
        </a>
        <button
          onClick={onClose}
          className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white text-xl transition-colors"
        >
          &times;
        </button>
      </div>
      <img
        src={src}
        alt=""
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}

function ImageGallery({ images }) {
  const [lightboxSrc, setLightboxSrc] = useState(null)

  if (!images || images.length === 0) return null

  const count = images.length
  const imgClass = "object-cover cursor-pointer hover:opacity-90 transition-opacity"

  return (
    <>
      {count === 1 ? (
        <div className="mt-3 rounded-xl overflow-hidden">
          <img src={images[0].url} alt="" className={`w-full max-h-96 ${imgClass}`} onClick={() => setLightboxSrc(images[0].url)} />
        </div>
      ) : (
        <div className={`mt-3 grid grid-cols-2 gap-1 rounded-xl overflow-hidden`}>
          {images.slice(0, 4).map((img) => (
            <img key={img.id} src={img.url} alt="" className={`w-full ${count <= 2 ? 'h-48' : 'h-36'} ${imgClass}`} onClick={() => setLightboxSrc(img.url)} />
          ))}
        </div>
      )}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </>
  )
}

function ThreeDotMenu({ isAuthor, isOwnerOrAdmin, pinned, onEdit, onDelete, onPin, onCategoryChange, categories = [], currentCategoryId, translations = {}, teamSlug, postId, currentUser, teamId, colors }) {
  const [open, setOpen] = useState(false)
  const [showCategories, setShowCategories] = useState(false)
  const [copied, setCopied] = useState(false)
  const [promoModalOpen, setPromoModalOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const canEdit = isAuthor
  const canDelete = isAuthor || isOwnerOrAdmin
  const canPin = isOwnerOrAdmin
  const canChangeCategory = isOwnerOrAdmin && categories.length > 0

  const handleCopyLink = async () => {
    const postUrl = buildTeamShareUrl(`/${teamSlug}/community/${postId}`, {}, currentUser?.inviteCode)
    if (isOwnerOrAdmin) {
      setOpen(false)
      setPromoModalOpen(true)
      return
    }
    try {
      await navigator.clipboard.writeText(postUrl)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = postUrl
      textarea.style.cssText = 'position:fixed;opacity:0;'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    setOpen(false)
  }

  if (!canEdit && !canDelete && !canPin && !canChangeCategory) return null

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => { setOpen((v) => !v); setShowCategories(false) }}
        className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        aria-label="Post options"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
          {canEdit && (
            <button
              onClick={() => { setOpen(false); onEdit() }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {translations.edit || 'Edit'}
            </button>
          )}
          {canChangeCategory && (
            <div>
              <button
                onClick={() => setShowCategories((v) => !v)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span>{translations.change_category || 'Change Category'}</span>
                <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showCategories ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {showCategories && (
                <div className="border-t border-gray-100 py-1 bg-gray-50">
                  <button
                    onClick={() => { setOpen(false); setShowCategories(false); onCategoryChange(null) }}
                    className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${!currentCategoryId ? 'text-blue-600 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {translations.no_category || 'No category'}
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => { setOpen(false); setShowCategories(false); onCategoryChange(cat.id) }}
                      className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${cat.id === currentCategoryId ? 'text-blue-600 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {canPin && (
            <button
              onClick={() => { setOpen(false); onPin() }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {pinned ? (translations.unpin || 'Unpin') : (translations.pin || 'Pin')}
            </button>
          )}
          {teamSlug && (
            <button
              onClick={handleCopyLink}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {copied ? (translations.link_copied || 'Copied!') : (translations.copy_link || 'Copy Link')}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => { setOpen(false); onDelete() }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              {translations.delete || 'Delete'}
            </button>
          )}
        </div>
      )}
      <PromotionLinkModal
        isOpen={promoModalOpen}
        onClose={() => setPromoModalOpen(false)}
        baseUrl={buildTeamShareUrl(`/${teamSlug}/community/${postId}`, {}, currentUser?.inviteCode)}
        teamId={teamId}
        inviteCode={currentUser?.inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </div>
  )
}

export function EditPostForm({ post, categories, teamId, onSaved, onCancel, colors, translations = {} }) {
  const [title, setTitle] = useState(post.title || '')
  const [body, setBody] = useState(post.body || '')
  const [categoryId, setCategoryId] = useState(post.category?.id || '')
  const [existingImages, setExistingImages] = useState(post.images || [])
  const [newImages, setNewImages] = useState([])
  const [newImagePreviews, setNewImagePreviews] = useState([])
  const [removeImageIds, setRemoveImageIds] = useState([])
  const [saving, setSaving] = useState(false)
  const [compressing, setCompressing] = useState(false)
  const [error, setError] = useState(null)
  const newImagePreviewsRef = useRef(newImagePreviews)
  newImagePreviewsRef.current = newImagePreviews

  useEffect(() => {
    return () => { newImagePreviewsRef.current.forEach(url => URL.revokeObjectURL(url)) }
  }, [])

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files)
    e.target.value = ''
    const remaining = 4 - existingImages.length - newImages.length
    const toAdd = files.slice(0, remaining)
    if (toAdd.length === 0) return

    setError(null)
    setCompressing(true)
    try {
      const compressed = await compressImages(toAdd)
      setNewImages((prev) => [...prev, ...compressed])
      setNewImagePreviews((prev) => [...prev, ...compressed.map((f) => URL.createObjectURL(f))])
    } catch (err) {
      setError('Failed to process image. Please try a different file.')
    } finally {
      setCompressing(false)
    }
  }

  const handleRemoveExisting = (imgId) => {
    setExistingImages((prev) => prev.filter((i) => i.id !== imgId))
    setRemoveImageIds((prev) => [...prev, imgId])
  }

  const handleRemoveNew = (idx) => {
    URL.revokeObjectURL(newImagePreviews[idx])
    setNewImages((prev) => prev.filter((_, i) => i !== idx))
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    if (!body.trim() || saving || compressing) return
    setSaving(true)
    setError(null)
    try {
      const data = await updateCommunityPost(teamId, post.id, {
        title: title.trim() || undefined,
        body: body.trim(),
        communityategoryId: categoryId || undefined,
        images: newImages.length > 0 ? newImages : undefined,
        removeImageIds: removeImageIds.length > 0 ? removeImageIds : undefined,
      })
      onSaved(data)
    } catch (err) {
      setError(err.message || 'Failed to update post')
    } finally {
      setSaving(false)
    }
  }

  const totalImages = existingImages.length + newImages.length

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={title}
        onChange={(e) => { setTitle(e.target.value); setError(null) }}
        placeholder={translations.title_placeholder || 'Title (optional)'}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 text-gray-800"
      />
      <AutoResizeTextarea
        value={body}
        onChange={(e) => { setBody(e.target.value); setError(null) }}
        rows={3}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 text-gray-800 resize-none"
        style={{ minHeight: '80px' }}
      />
      {categories && categories.length > 0 && (
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 text-gray-700 bg-white"
        >
          <option value="">{translations.no_category || 'No category'}</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      )}
      {/* Existing images */}
      {(existingImages.length > 0 || newImagePreviews.length > 0 || compressing) && (
        <div className="flex flex-wrap gap-2">
          {existingImages.map((img) => (
            <div key={img.id} className="relative">
              <img src={img.url} alt="" className="w-20 h-20 object-cover rounded-lg" />
              <button
                onClick={() => handleRemoveExisting(img.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                &times;
              </button>
            </div>
          ))}
          {newImagePreviews.map((url, idx) => (
            <div key={idx} className="relative">
              <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg" />
              <button
                onClick={() => handleRemoveNew(idx)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                &times;
              </button>
            </div>
          ))}
          {compressing && (
            <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
            </div>
          )}
        </div>
      )}
      {totalImages < 4 && (
        <label className={`inline-flex items-center gap-1.5 text-sm transition-colors ${compressing ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 cursor-pointer hover:text-gray-700'}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {translations.add_image || 'Add image'}
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageChange} disabled={compressing} />
        </label>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          {translations.cancel || 'Cancel'}
        </button>
        <button
          onClick={handleSave}
          disabled={!body.trim() || saving || compressing}
          className="px-4 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
          style={{ backgroundColor: colors?.primary || '#3366FF' }}
        >
          {saving ? (translations.saving || 'Saving...') : (translations.save || 'Save')}
        </button>
      </div>
    </div>
  )
}

export default function CommunityPostCard({
  post,
  teamId,
  teamSlug,
  currentUser,
  colors,
  isOwnerOrAdmin,
  canInteract = true,
  onRequestJoin,
  onPostUpdated,
  onPostDeleted,
  translations = {},
  categories = [],
  expandedView = false,
}) {
  const [showDetail, setShowDetail] = useState(false)
  const [lightboxSrc, setLightboxSrc] = useState(null)
  const [editing, setEditing] = useState(false)
  const [liked, setLiked] = useState(post.liked)
  const [likesCount, setLikesCount] = useState(post.likes_count || 0)
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0)
  const [comments, setComments] = useState(post.recent_comments || [])
  const [showComments, setShowComments] = useState(false)
  const [liking, setLiking] = useState(false)
  const [viewStats, setViewStats] = useState(
    post.student_views_count !== undefined
      ? { student_views_count: post.student_views_count, visitor_views_count: post.visitor_views_count }
      : null
  )

  // In expandedView (public show page), fetch view stats for owner/admin
  useEffect(() => {
    if (!expandedView || !isOwnerOrAdmin || !post?.id || !teamId) return
    recordCommunityPostView(teamId, post.id)
      .then((data) => {
        if (data.student_views_count !== undefined) {
          setViewStats(data)
        }
      })
      .catch(() => {})
  }, [expandedView, isOwnerOrAdmin, post?.id, teamId])

  const isAuthor = currentUser && (
    (post.author?.type === 'teacher' && currentUser.teacherProfileId === post.author?.id) ||
    (post.author?.type === 'student' && currentUser.id === post.author?.id)
  )
  const BODY_CLAMP_LINES = 3

  const handleLike = async () => {
    if (!currentUser) {
      window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
      return
    }
    if (!canInteract) {
      onRequestJoin && onRequestJoin()
      return
    }
    if (liking) return
    setLiking(true)
    const wasLiked = liked
    if (!wasLiked) trackEvent('community_post_like', { post_title: post.title || 'untitled' })
    setLiked(!wasLiked)
    setLikesCount((c) => wasLiked ? c - 1 : c + 1)
    try {
      await toggleCommunityLike(teamId, post.id)
    } catch (err) {
      setLiked(wasLiked)
      setLikesCount((c) => wasLiked ? c + 1 : c - 1)
    } finally {
      setLiking(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm(translations.confirm_delete || 'Delete this post?')) return
    try {
      await deleteCommunityPost(teamId, post.id)
      onPostDeleted && onPostDeleted(post.id)
    } catch (err) {
      console.error('Failed to delete post:', err)
    }
  }

  const handlePin = async () => {
    try {
      const data = await pinCommunityPost(teamId, post.id, !post.pinned)
      onPostUpdated && onPostUpdated(data)
    } catch (err) {
      console.error('Failed to pin post:', err)
    }
  }

  const handleCategoryChange = async (categoryId) => {
    try {
      const data = await updateCommunityPost(teamId, post.id, {
        communityategoryId: categoryId,
      })
      onPostUpdated && onPostUpdated(data)
    } catch (err) {
      console.error('Failed to update category:', err)
    }
  }

  const handleSaved = (data) => {
    setEditing(false)
    onPostUpdated && onPostUpdated(data)
  }

  const handleCommentCreated = (data) => {
    const comment = data.comment || data
    setComments((prev) => [...prev, comment])
    setCommentsCount((c) => c + 1)
  }

  const handleCommentDeleted = (commentId) => {
    setComments((prev) => prev.filter((c) => c.id !== commentId))
    setCommentsCount((c) => Math.max(0, c - 1))
  }

  const mostRecentComment = comments.length > 0 ? comments[comments.length - 1] : null

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3">
        <UserAvatar
          src={post.author?.avatar_url}
          name={post.author?.name}
          size="sm"
          fallbackColor={colors?.primary || '#3366FF'}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-900">{post.author?.name}</span>
            {post.pinned && (
              <span className="text-xs text-gray-500 flex items-center gap-0.5">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                {translations.pinned || 'Pinned'}
              </span>
            )}
            {post.category && (
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: `${colors?.primary || '#3366FF'}15`, color: colors?.primary || '#3366FF' }}
              >
                {post.category.name}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400">{timeAgo(post.created_at)}</span>
        </div>
        <ThreeDotMenu
          isAuthor={isAuthor}
          isOwnerOrAdmin={isOwnerOrAdmin}
          pinned={post.pinned}
          onEdit={() => setEditing(true)}
          onDelete={handleDelete}
          onPin={handlePin}
          onCategoryChange={handleCategoryChange}
          categories={categories}
          currentCategoryId={post.category?.id}
          translations={translations}
          teamSlug={teamSlug}
          postId={post.id}
          currentUser={currentUser}
          teamId={teamId}
          colors={colors}
        />
      </div>

      {/* Body */}
      <div className="mt-3">
        {editing ? (
          <EditPostForm
            post={post}
            categories={categories}
            teamId={teamId}
            onSaved={handleSaved}
            onCancel={() => setEditing(false)}
            colors={colors}
            translations={translations}
          />
        ) : expandedView ? (
          /* Expanded view: full content, all images, no modal trigger */
          <div>
            {post.title && (
              <h3 className="font-bold text-gray-900 text-lg mb-2">{post.title}</h3>
            )}
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
              <Linkify text={post.body} embedVideos="compact" />
            </div>
            {post.images && post.images.length > 0 && (
              <div className="mt-4 space-y-3">
                {post.images.map((img) => (
                  <img
                    key={img.id}
                    src={img.url}
                    alt=""
                    className="w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setLightboxSrc(img.url)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="cursor-pointer" onClick={() => { trackEvent('community_post_click', { post_title: post.title || 'untitled' }); setShowDetail(true) }}>
            <div className="flex gap-4">
              <div className="flex-1 min-w-0">
                {post.title && (
                  <h3 className="font-bold text-gray-900 text-base mb-1">{post.title}</h3>
                )}
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words line-clamp-3">
                  <Linkify text={post.body} embedVideos={false} />
                </div>
                {post.body && post.body.length > 150 && (
                  <span
                    className="mt-1 text-sm font-medium inline-block"
                    style={{ color: colors?.primary || '#3366FF' }}
                  >
                    {translations.read_more || 'Read more'}
                  </span>
                )}
              </div>
              {/* Thumbnail on the right */}
              {post.images && post.images.length > 0 && (
                <div
                  className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden"
                  onClick={(e) => { e.stopPropagation(); setLightboxSrc(post.images[0].url) }}
                >
                  <img src={post.images[0].url} alt="" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                </div>
              )}
            </div>
            {/* Video thumbnail outside line-clamp */}
            {post.body && post.body.match(/(https?:\/\/[^\s<]+)/g)?.map((url, i) => {
              const videoInfo = detectVideoPlatform(url)
              return videoInfo && videoInfo.type !== 'audio' ? <VideoThumbnail key={i} url={url} /> : null
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      {!editing && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
          >
            <svg
              className="w-4 h-4"
              fill={liked ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>{likesCount > 0 ? likesCount : ''}</span>
          </button>
          <button
            onClick={() => {
              if (!canInteract) {
                onRequestJoin && onRequestJoin()
              } else {
                setShowComments((v) => !v)
              }
            }}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>{commentsCount > 0 ? commentsCount : ''}</span>
          </button>
          <div className="ml-auto flex items-center gap-2">
            {!showComments && mostRecentComment && (
              <span className="text-xs text-gray-400">
                {(translations.new_comment || 'New comment %{ago}').replace('%{ago}', timeAgo(mostRecentComment.created_at))}
              </span>
            )}
            <ShareButton
              url={buildTeamShareUrl(`/${teamSlug}/community/${post.id}`, {}, currentUser?.inviteCode)}
              title={post.title || ''}
              colors={colors}
              translations={translations}
              utmContent={`post-${post.id}`}
              currentUser={currentUser}
              teamId={teamId}
            />
          </div>
        </div>
      )}

      {/* View Stats (managers only) */}
      {isOwnerOrAdmin && viewStats && (
        <ViewStats
          studentViews={viewStats.student_views_count}
          visitorViews={viewStats.visitor_views_count}
          translations={translations}
          className="mt-1 px-1"
        />
      )}

      {/* Comments */}
      {!editing && (showComments || expandedView) && canInteract && (
        <div className="mt-3 space-y-2">
          <CommunityCommentList
            postId={post.id}
            teamId={teamId}
            recentComments={comments}
            totalCount={commentsCount}
            currentUser={currentUser}
            isOwnerOrAdmin={isOwnerOrAdmin}
            onCommentDeleted={handleCommentDeleted}
            translations={translations}
          />
          <CommunityCommentInput
            postId={post.id}
            teamId={teamId}
            currentUser={currentUser}
            canInteract={canInteract}
            onRequestJoin={() => onRequestJoin && onRequestJoin()}
            onCommentCreated={handleCommentCreated}
            translations={translations}
          />
        </div>
      )}

      {/* Folded comments for non-members */}
      {!editing && !canInteract && commentsCount > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <button
            onClick={() => onRequestJoin && onRequestJoin()}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span className="font-medium">{commentsCount}</span>
            {' '}{translations.comments_count_label || 'comments'}
            {' · '}
            <span style={{ color: colors?.primary || '#3366FF' }}>
              {translations.join_to_view || 'Join to view discussion'}
            </span>
          </button>
        </div>
      )}

      {/* Detail modal */}
      {showDetail && (
        <CommunityPostDetailModal
          post={post}
          teamId={teamId}
          currentUser={currentUser}
          colors={colors}
          isAuthor={isAuthor}
          isOwnerOrAdmin={isOwnerOrAdmin}
          canInteract={canInteract}
          liked={liked}
          likesCount={likesCount}
          commentsCount={commentsCount}
          comments={comments}
          categories={categories}
          onClose={() => setShowDetail(false)}
          onLike={handleLike}
          onEdit={() => { setShowDetail(false); setEditing(true) }}
          onDelete={() => { setShowDetail(false); handleDelete() }}
          onPin={() => { handlePin(); }}
          onCategoryChange={handleCategoryChange}
          onPostUpdated={(data) => { const updated = data.post || data; onPostUpdated && onPostUpdated(updated) }}
          onCommentCreated={handleCommentCreated}
          onCommentDeleted={handleCommentDeleted}
          onRequestJoin={() => onRequestJoin && onRequestJoin()}
          translations={translations}
          teamSlug={teamSlug}
        />
      )}

      {/* Lightbox for thumbnail click */}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

    </div>
  )
}
